import axios from "axios";
import _, { values } from "lodash";
import {
  filteredOptions,
  sortedOptions,
  largeFormatCheck,
} from "../../../config/options";
import {
  CANVAS_PHOTO_PRINT,
  COLLAGE_PRINTS,
  METAL_PRINTS,
  SINGLE_CANVAS_FEATURED_PRODUCTS,
  TRIPTYCH_FLOW_PRINTS,
  TRIPTYCH_PRINTS,
  WALL_DISPLAY,
  WALL_DISPLAY_PRINTS,
} from "../../../constants/collections";
import {
  DIGITAL_PROOF,
  BLIND_SHIPPING,
  FLOATER_FRAME,
} from "../../../constants/productOptions";

import { pageType } from "../services/optionsHelpers";
import { getCollection } from "./productHelpers";
export default class OptionsUtility {
  constructor({
    state,
    options,
    currentItems,
    dimensions,
    item,
    setOptions,
    setCartItems,
    setLoading,
    collectionName,
    selectedProduct,
  }) {
    this.options = options || state?.threekit.options;
    if (state?.threekit.cartItems.length) {
      this.currentItems = currentItems || state?.threekit.cartItems[0];
    } else {
      this.currentItems = currentItems || state?.threekit.cartItems;
    }
    this.setOptions = setOptions; // This method is defined in scripts/config/FeatureSwitches.js
    this.item = item;
    this.dimensions = dimensions || state?.playerState.dimensions;
    this.setCartItems = setCartItems;
    this.setLoading = setLoading;
    this.selectedProduct = selectedProduct || state?.threekit.selectedProduct;
    this.collectionName = collectionName || getCollection(this.item);
    this.quantity = state?.threekit.quantity || 1;
  }

  findOption = (optionHandle, options = this.options) =>
    options.find((option) => option.handle == optionHandle);

  getSelectedOption = (handle, options = this.options) =>
    this.findOption(handle, options)?.values.find((option) => option.selected);

  getHandle = (value) => {
    const { canWidth: width, canHeight: height } = this.dimensions;

    if (
      this.collectionName.includes("triptych") ||
      this.item?.handle.includes("triptych")
    ) {
      return this.getTriptychFrameHandle(height, width, value);
    }
    return (
      "floater-frame-" +
      value.color +
      "-" +
      value.depth +
      "-" +
      height +
      "-" +
      width
    );
  };

  getTriptychFrameHandle = (height, width, value) => {
    if (
      this.collectionName == "triptych-flow-prints" ||
      this.item?.handle.includes("flow-triptych")
    ) {
      return (
        "floater-frame-flow-triptych-" +
        value.color +
        "-" +
        value.depth +
        "-" +
        height +
        "-" +
        width
      );
    } else {
      return (
        "floater-frame-classic-triptych-" +
        value.color +
        "-" +
        value.depth +
        "-" +
        height +
        "-" +
        width
      );
    }
  };

  async updateValuesForSelectedOption(option, label, valueSku) {
    return await Promise.all(
      option.values.map(async (value) => {
        if (value.label == label) {
          if (option.handle === FLOATER_FRAME && value.value != "None") {
            const handle = this.getHandle(value);
            const response = await axios.get(`/products/${handle}.json`);
            const product = response.data.product;
            const newValue = {
              ...value,
              price: parseFloat(product.variants[0].price),
              sku: product.variants[0].sku,
              variant: product.variants[0],
              selected: true,
            };
            option.product = product;
            return newValue;
          }

          value.selected = true;
          value.variant = option.product?.variants.find(
            (variant) => variant.sku == (valueSku || value.sku)
          );
        } else {
          value.selected = false;
        }
        return value;
      })
    );
  }

  async changeOptionToLabel(optionName, label, valueSku) {
    const newOptions = await Promise.all(
      _.cloneDeep(this.options).map(async (option) => {
        if (option.name == optionName) {
          option.values = await this.updateValuesForSelectedOption(
            option,
            label,
            valueSku
          );
        }

        if (optionName == "CANVAS DEPTH" && option.name == "FLOATER FRAME") {
          option.values = await this.updateValuesForSelectedOption(
            option,
            "None",
            "CP-OPT-FRAME_NONE-0000-001"
          );
        }
        return option;
      })
    );

    this.setOptions(newOptions);
  }

  fetchOptions = () => {
    axios({
      method: "get",
      url: `/collections/product-options/products.json`,
    }).then((resp) => {
      const products = resp.data.products;
      const newOptions = this.options.map((option) => {
        const foundProduct = products.find(
          (product) => product.handle == option.handle
        );
        return { ...option, product: foundProduct };
      });
      this.setOptions && this.setOptions(newOptions);
    });
  };

  fetchCartItems = () => {
    axios({
      method: "get",
      url: `/cart.js`,
    }).then((resp) => {
      const currentItems = resp.data.items;
      this.setCartItems(currentItems);
    });
  };

  get filteredOptions() {
    let newOptions = null;
    if (this.item == null) {
      newOptions = filteredOptions(
        pageType,
        sortedOptions(this.options),
        false
      );
    } else {
      newOptions = filteredOptions(
        this.item.sku?.split("-")[1],
        this.options,
        true
      );
    }
    newOptions = this.blindShippingPriceChange(newOptions);

    // If we're supposed to show the higher-priced proof, remove the lower-priced option
    if (this.hasHigherPricedDigitalProof) {
      newOptions = newOptions.map((option) => {
        if (option.handle !== DIGITAL_PROOF) {
          return option;
        } else {
          const updatedOption = { ...option }; // Making a copy because for some reason option is read only
          updatedOption.values = updatedOption.values.filter(
            (variant) => variant.price !== 5
          );

          // Reverse the order of the values so the "Yes" option comes first
          updatedOption.values = updatedOption.values.reverse();

          return updatedOption;
        }
      });
    } else {
      // Conversely, remove the higher-priced option if it shouldn't be displayed
      newOptions = newOptions.map((option) => {
        if (option.handle !== DIGITAL_PROOF) {
          return option;
        } else {
          const updatedOption = { ...option }; // Making a copy because for some reason option is read only
          updatedOption.values = updatedOption.values.filter(
            (variant) => variant.price !== 15
          );
          return updatedOption;
        }
      });
    }

    if (!this.metalEaselCheck) {
      newOptions = newOptions.filter((option) => {
        if (typeof option === "undefined") {
          return false;
        }
        return option.handle !== "metal-easel";
      });
    }
    if (this.largeFormat || this.cartLargeFormat) {
      newOptions = newOptions.filter(
        (option) => option.handle !== "canvas-depth"
      );
      newOptions = newOptions.map((option) => {
        if (option.handle.includes("floater-frame")) {
          let selectedVal = option.values.find((value) => value.selected);
          this.checkFrameHandles(option, selectedVal);
          let newOption = { ...option };
          newOption.values = newOption.values.filter(
            (val) => !val.value.includes("1.25")
          );
          if (
            option.values.find((value) => value.selected).value.includes("1.25")
          ) {
            newOption.values[0] = { ...newOption.values[0], selected: true };
          }
          return newOption;
        } else return option;
      });
      return newOptions;
    } else {
      newOptions = newOptions.map((option) => {
        if (option.handle.includes("floater-frame")) {
          let selectedVal = option.values.find((value) => value.selected);
          this.checkFrameHandles(option, selectedVal);
          let depth = newOptions
            .find((option) => option.handle == "canvas-depth")
            .values.find((val) => val.value == "0.75");
          let newOption = { ...option };
          if (depth.value == "0.75" && depth.selected) {
            newOption.values = newOption.values.filter(
              (val) => !val.value.includes('2"')
            );
            if (
              option.values.find((value) => value.selected).value.includes('2"')
            ) {
              newOption.values[0] = { ...newOption.values[0], selected: true };
            }
          } else {
            newOption.values = newOption.values.filter(
              (val) => !val.value.includes("1.25")
            );
            if (
              option.values
                .find((value) => value.selected)
                .value.includes("1.25")
            ) {
              newOption.values[0] = { ...newOption.values[0], selected: true };
            }
          }
          return newOption;
        } else return option;
      });
    }
    return newOptions;
  }

  blindShippingPriceChange = (newOptions) => {
    newOptions = newOptions.map((option) => {
      if (option.handle !== BLIND_SHIPPING) {
        return option;
      }

      const b2b = false;

      if (b2b) {
        const updatedOption = { ...option }; // Making a copy because for some reason option is read only
        updatedOption.values = updatedOption.values.filter(
          (variant) => !variant.value.includes("$15.00")
        );

        return updatedOption;
      } else {
        const updatedOption = { ...option }; // Making a copy because for some reason option is read only
        updatedOption.values = updatedOption.values.filter(
          (variant) => !variant.value.includes("$0.00")
        );

        return updatedOption;
      }
    });
    return newOptions;
  };

  async checkFrameHandles(option, selectedVal) {
    const handle = this.getHandle(selectedVal, false);
    if (option.product !== undefined && selectedVal.value != "None") {
      if (option.product.handle != handle) {
        await this.changeOptionToLabel(
          "FLOATER FRAME",
          "None",
          "CP-OPT-FRAME_NONE-0000-001"
        );
      }
    }
    return;
  }

  get hasHigherPricedDigitalProof() {
    return [
      TRIPTYCH_PRINTS,
      TRIPTYCH_FLOW_PRINTS,
      WALL_DISPLAY,
      WALL_DISPLAY_PRINTS,
    ].includes(this.collectionName);
  }

  get metalEaselCheck() {
    if (
      this.collectionName == METAL_PRINTS &&
      (this.selectedProduct.handle == "8x10-metal-photo-print" ||
        this.selectedProduct.handle == "10x8-metal-photo-print")
    )
      return true;
    return false;
  }

  get cartHasNDS() {
    return this.currentItems.find(
      (item) => item.sku == "CP-OPT-NDS_YES-0000-001"
    );
  }

  get cartHasDP() {
    return this.currentItems.filter((item) =>
      ["CP-OPT-DGTL_PROOF-0000-001", "CP-OPT-DGTL_PROOF-0000-003"].includes(
        item.sku
      )
    );
  }

  get itemHasNDS() {
    let optionNDS = this.options.find(
      (item) =>
        item.handle == "next-day-shipping" ||
        item.handle == "next-day-shipping-promo"
    );
    if (optionNDS === undefined) return false;
    return optionNDS.values[0].selected;
  }

  get itemHasDP() {
    let optionDP = this.options.find((item) => item.handle == "digital-proof");
    return optionDP?.values[0].selected || optionDP?.values[2].selected;
  }

  get productOrCartHasDigitalProof() {
    const digitalProofSKUs = [
      "CP-OPT-DGTL_PROOF-0000-001",
      "CP-OPT-DGTL_PROOF-0000-003",
    ];
    return (
      this.currentItems.find((item) => digitalProofSKUs.includes(item.sku)) ||
      digitalProofSKUs.includes(this.getSelectedOption("digital-proof")?.sku)
    );
  }

  get productOrCartHasNDS() {
    let getNDS =
      this.getSelectedOption("next-day-shipping") ||
      this.getSelectedOption("next-day-shipping-promo");
    return (
      this.currentItems.find((item) => item.sku == "CP-OPT-NDS_YES-0000-001") ||
      (getNDS !== undefined && getNDS.sku === "CP-OPT-NDS_YES-0000-001")
    );
  }

  /**
   * Returns boolean based on if current item does not have NDS but the cart contains NDS
   * and NDS linkID for both the item and NDS Match.
   * Used in the cart when a user turns off NDS but has not saved yet
   *
   * @returns {boolean}
   */

  get itemInCartHoldingNDS() {
    if (this.itemHasNDS == false && this.cartHasNDS && this.item != null)
      if (this.item.properties._linkId == this.cartHasNDS.properties._linkId) {
        return true;
      }
    return false;
  }

  /**
   * Returns boolean based on if current item does not have DP but the cart contains DP
   * and DP linkID for both the item and DP Match.
   * Used in the cart when a user turns off DP but has not saved yet
   *
   * @returns {boolean}
   */

  get itemInCartHoldingDP() {
    if (
      this.itemHasDP == false &&
      this.cartHasDP.length == 1 &&
      this.productOrCartHasDigitalProof &&
      this.item != null
    )
      if (
        this.item.properties._linkId ==
        this.productOrCartHasDigitalProof.properties._linkId
      ) {
        return true;
      }
    return false;
  }

  get shouldShowNDS() {
    if (this.itemHasNDS) {
      return this.itemHasNDS;
    }
    if (this.itemInCartHoldingNDS) return this.itemInCartHoldingNDS;
    let dpInProductOrCart = !this.productOrCartHasDigitalProof;
    let cartHasNDS = !this.cartHasNDS;
    let shouldShow = dpInProductOrCart && cartHasNDS;
    return shouldShow;
  }

  get shouldShowDP() {
    if (this.itemHasDP) {
      return this.itemHasDP;
    }
    let shouldShow = !this.productOrCartHasNDS;
    return shouldShow;
  }

  get shouldGetLargeFormat() {
    return (
      this.collectionName == SINGLE_CANVAS_FEATURED_PRODUCTS ||
      this.collectionName == COLLAGE_PRINTS ||
      this.collectionName == TRIPTYCH_PRINTS ||
      this.collectionName == TRIPTYCH_FLOW_PRINTS ||
      this.collectionName == CANVAS_PHOTO_PRINT
    );
  }

  get notLargeProductOption() {
    if (this.item !== undefined)
      return !(
        this.item.handle == "classic-triptych" ||
        this.item.handle == "classic-flow-triptych"
      );
    else if (this.selectedProduct !== undefined) {
      return !(
        this.selectedProduct.handle == "classic-triptych" ||
        this.selectedProduct.handle == "classic-flow-triptych"
      );
    }
  }

  get largeFormat() {
    const { canWidth: width, canHeight: height } = this.dimensions;

    if (width != 0 && height != 0) {
      return this.shouldGetLargeFormat && largeFormatCheck(height, width);
    }
    if (
      this.selectedProduct !== undefined &&
      this.selectedProduct.handle?.split("-")[2]
    ) {
      let height = parseInt(
        this.selectedProduct.handle?.split("-")[2].split("x")[0]
      );
      let width = parseInt(
        this.selectedProduct.handle?.split("-")[2].split("x")[1]
      );
      return largeFormatCheck(height, width);
    }
  }

  get cartLargeFormat() {
    if (this.item !== undefined && this.item.sku?.split("-")[2]) {
      if (this.item.sku?.split("-")[1] == "CAN") {
        let height = parseInt(this.item.sku?.split("-")[2].split("X")[0]);
        let width = parseInt(this.item.sku?.split("-")[2].split("X")[1]);
        return largeFormatCheck(height, width);
      } else {
        if (this.item.handle.includes("triptych")) {
          if (this.item.handle.includes("classic")) {
            this.dimensions = { canWidth: "48", canHeight: "32" };
          } else {
            this.dimensions = { canWidth: "30", canHeight: "20" };
          }
          return;
        }
        let height = parseInt(this.item.handle?.split("-")[2].split("x")[0]);
        let width = parseInt(this.item.handle?.split("-")[2].split("x")[1]);
        return largeFormatCheck(height, width);
      }
    }
  }

  get customPrice() {
    const values = this.filteredOptions.map((option) => {
      const selected = option.values.find((value) => value.selected);
      let frame = this.currentItems.find(
        (option) =>
          option.sku?.includes("FLOATER_FRAME") &&
          selected.sku.includes("FLOATER_FRAME")
      );
      if (frame !== undefined && this.item !== undefined) {
        return (
          parseFloat(((frame?.price / 100.0) * this.quantity).toFixed(2)) || 0
        );
      } else if (
        selected.sku.includes("DGTL_PROOF") ||
        selected.sku.includes("NDS")
      ) {
        return selected?.price || 0;
      } else {
        return parseFloat((selected?.price * this.quantity).toFixed(2)) || 0;
      }
    });
    return _.sum(values);
  }

  floatToCurrency(float) {
    if (float === undefined) {
      return "";
    }
    let currency = `${float}`;
    if (currency.split(".").length === 1) {
      return currency + ".00";
    }
    if (currency.split(".")?.[1].length === 1) {
      return currency + "0";
    }
    return currency;
  }

  get totalPrice() {
    let variant =
      this.selectedProduct.variants.find(
        (variant) => variant.title === this.selectedProduct.title
      ) || this.selectedProduct.variants[0];
    let price = variant.price;

    if (typeof price == "number") {
      price = price / 100;
    }

    let totalPrice =
      parseFloat(price * this.quantity) + parseFloat(this.customPrice);
    return parseFloat(totalPrice.toFixed(2));
  }

  get totalPriceInCurrency() {
    return this.selectedProduct?.variants?.[0]
      ? this.floatToCurrency(this.totalPrice)
      : "";
  }

  get depth() {
    return this.getSelectedOption("canvas-depth");
  }

  get border() {
    return this.getSelectedOption("image-border-type");
  }

  get posterBorder() {
    return this.getSelectedOption("border-type");
  }

  get paperType() {
    return this.getSelectedOption("paper-type");
  }

  get frame() {
    let frame = this.filteredOptions
      .find((option) => option.handle == "floater-frame")
      ?.values?.find((value) => value.selected);
    return frame;
  }

  get optionsForCart() {
    const productOptions = {};
    this.options
      .map((a) => a.handle)
      .forEach((productHandle) => {
        const option = this.findOption(productHandle, this.filteredOptions);
        const value = this.getSelectedOption(
          productHandle,
          this.filteredOptions
        );
        if (option) {
          productOptions[option.name] = value.value;
        }
      });
    if (this.largeFormat) {
      productOptions["CANVAS DEPTH"] = "1.5";
    }
    if (productOptions["IMAGE BORDER TYPE"]) {
      productOptions["IMAGE BORDER TYPE"] =
        this.getSelectedOption("image-border-type").value;
    }
    if (productOptions["BORDER"]) {
      productOptions["BORDER"] = this.getSelectedOption("border-type").value;
    }
    if (productOptions["PAPER TYPE"]) {
      productOptions["PAPER TYPE"] = this.getSelectedOption("paper-type").value;
    }
    return productOptions;
  }
  updateProductOptions = (cartProduct, currentOptions) => async (event) => {
    this.setLoading(true);
    const linkId = cartProduct.properties._linkId;
    const product_key = cartProduct.key;
    const updatePayload = {};

    currentOptions.forEach((i) => {
      updatePayload[i.key] = 0;
    });

    await axios({
      method: "post",
      url: `/cart/update.json`,
      data: { updates: updatePayload },
    });
    const newProperties = {
      ...cartProduct.properties,
      ...this.optionsForCart,
    };

    const changePayload = {
      id: product_key,
      properties: _.merge(cartProduct.properties, newProperties),
      quantity: cartProduct.quantity,
    };

    await axios({
      method: "post",
      url: `/cart/change.json`,
      data: changePayload,
    });

    const newOptionsPayload = this.customProductsForCart(
      linkId,
      cartProduct.quantity
    );
    if (currentOptions.length == 0 && newOptionsPayload.length == 0) {
      location.reload();
    }
    await axios({
      method: "post",
      url: `/cart/add.json`,
      data: { items: newOptionsPayload },
    });
    location.reload();
  };

  customProductsForCart(linkId, quantity) {
    const customProducts = [];
    _.each(this.filteredOptions, (option) => {
      const selectedValue = this.getSelectedOption(option.handle);
      const selectedVariant = option.product?.variants?.find(
        (variant) => variant.sku == selectedValue.sku
      );
      if (
        selectedValue.sku.includes("FLOATER_FRAME") &&
        selectedValue.price == 0
      ) {
        let frame = this.currentItems.find(
          (frame) => frame.properties._linkId == linkId
        );

        customProducts.push({
          properties: { _linkId: linkId },
          id: frame.id,
          quantity: quantity,
        });
      }

      if ((selectedValue && selectedValue.price > 0) || selectedValue.variant) {
        if (selectedValue.price == 0) {
          customProducts.push({
            properties: { _linkId: linkId },
            id: selectedVariant.id,
            quantity: 0,
          });
        } else if (
          selectedValue.sku == "CP-OPT-NDS_YES-0000-001" ||
          selectedValue.sku == "CP-OPT-PRTY_HNDLING_YES-0000-001" ||
          selectedValue.sku == "CP-OPT-DGTL_PROOF-0000-001"
        ) {
          customProducts.push({
            properties: { _linkId: linkId },
            id: selectedVariant.id,
            quantity: 1,
          });
        } else {
          customProducts.push({
            properties: { _linkId: linkId },
            id: selectedVariant.id,
            quantity: quantity,
          });
        }
      }
      if (
        selectedValue &&
        (selectedValue.sku.includes("OPT-BLEED") ||
          selectedValue.sku.includes("OPT-PAPER"))
      ) {
        customProducts.push({
          properties: { _linkId: linkId },
          id: selectedVariant.id,
          quantity: quantity,
        });
      }
    });

    return customProducts;
  }
}
